import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  ArrowRight, Building2, Wrench, Factory, ClipboardList,
  CheckCircle, BarChart2, Shield, Zap, Users, Layers, Cpu,
  XCircle, TrendingUp, ChevronRight, Timer, Award, Target,
  FileCheck, GitCompare, LayoutDashboard, PackageSearch,
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';

/* ─── helpers ─── */

function Section({ children, className = '', id }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase';
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    dark: 'bg-white/10 text-white/80 border border-white/20',
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

/** Counts from 0 → to on mount — used inside AnimatePresence panels (re-mounts on tab change) */
function Counter({ to, suffix = '', prefix = '' }) {
  const [val, setVal] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const startTime = Date.now();
    const DURATION = 1300;
    const tick = () => {
      const p = Math.min((Date.now() - startTime) / DURATION, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setVal(to);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [to]);
  return <>{prefix}{val}{suffix}</>;
}

/** Counts from 0 → to when element scrolls into view — for always-mounted KPI strip */
function InViewCounter({ to, suffix = '', prefix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [val, setVal] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!inView) return;
    const startTime = Date.now();
    const DURATION = 1500;
    const tick = () => {
      const p = Math.min((Date.now() - startTime) / DURATION, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setVal(to);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [inView, to]);
  return <span ref={ref}>{prefix}{val}{suffix}</span>;
}

/* ─── color tokens per industry ─── */
const IND_COLORS = {
  blue: {
    tab:    'bg-blue-600 text-white',
    text:   'text-blue-600',
    bg:     'bg-blue-50',
    border: 'border-blue-200',
    pill:   'bg-blue-600/10 text-blue-700 border-blue-200',
    glow:   'shadow-blue-500/20',
    stat:   'from-blue-500 to-blue-700',
  },
  emerald: {
    tab:    'bg-emerald-600 text-white',
    text:   'text-emerald-600',
    bg:     'bg-emerald-50',
    border: 'border-emerald-200',
    pill:   'bg-emerald-600/10 text-emerald-700 border-emerald-200',
    glow:   'shadow-emerald-500/20',
    stat:   'from-emerald-500 to-emerald-700',
  },
  amber: {
    tab:    'bg-amber-500 text-white',
    text:   'text-amber-600',
    bg:     'bg-amber-50',
    border: 'border-amber-200',
    pill:   'bg-amber-500/10 text-amber-700 border-amber-200',
    glow:   'shadow-amber-500/20',
    stat:   'from-amber-500 to-amber-600',
  },
  purple: {
    tab:    'bg-purple-600 text-white',
    text:   'text-purple-600',
    bg:     'bg-purple-50',
    border: 'border-purple-200',
    pill:   'bg-purple-600/10 text-purple-700 border-purple-200',
    glow:   'shadow-purple-500/20',
    stat:   'from-purple-500 to-purple-700',
  },
};

/* ─── industry data ─── */
const INDUSTRIES = {
  hvac: {
    icon: Building2,
    color: 'blue',
    titleEn: 'HVAC Manufacturers & Contractors',
    titleAr: 'مصنّعو ومقاولو التكييف والتهوية',
    taglineEn: 'From Any BoQ Format to ERP-Ready Catalog Matches — Automatically',
    taglineAr: 'من أي صيغة لقائمة الكميات إلى مخرجات جاهزة للـ ERP تلقائياً',
    descEn: "HVAC manufacturers and contractors deal with complex product hierarchies — grilles, diffusers, VAV boxes, dampers — each with unique sizing conventions. Consultants deliver BoQs in 10+ different formats. Tuly's HVAC-native engine maps every line item to your exact catalog model with full confidence scoring.",
    descAr: 'مصنّعو ومقاولو التكييف يتعاملون مع تسلسلات منتجات معقدة — شبكات، موزعات، صناديق VAV، مخمدات — بقواعد قياس مختلفة لكل منتج. تولي يربط كل سطر بنموذجك الدقيق في الكتالوج مع تقييم ثقة كامل.',
    pains: [
      { en: 'BoQs arrive in 10+ inconsistent formats from different consultants', ar: 'قوائم الكميات تصل بأكثر من 10 صيغ غير متسقة من استشاريين مختلفين' },
      { en: 'Sizing conventions differ: WxH grilles, Ø round ducts, Slots×Length diffusers', ar: 'قواعد القياس تختلف: شبكات عرض×ارتفاع، مجاري دائرية، موزعات فتحات×طول' },
      { en: 'Model code selection relies on individual engineer experience — error-prone', ar: 'اختيار كود الطراز يعتمد على خبرة المهندس الفردية — عرضة للأخطاء' },
      { en: 'Submittal packages take 2–3 days of manual preparation per project', ar: 'حزم المستندات تستغرق 2-3 أيام من التحضير اليدوي لكل مشروع' },
    ],
    features: [
      { en: 'HVAC-native sizing engine: WxH grilles, Ø round ducts, Slots+L linear diffusers', ar: 'محرك قياس خاص بـ HVAC: شبكات، مجاري دائرية، موزعات خطية' },
      { en: 'AI catalog mapping with confidence scoring and explainability per line', ar: 'ربط كتالوج بالذكاء الاصطناعي مع تقييم ثقة وتفسير لكل بند' },
      { en: 'Multi-supplier and multi-brand support within a single project', ar: 'دعم متعدد الموردين والعلامات في مشروع واحد' },
      { en: 'Complete submittal packages auto-generated in under 30 minutes', ar: 'حزم مستندات كاملة تُنشأ تلقائياً في أقل من 30 دقيقة' },
    ],
    stats: [
      { to: 85, suffix: '%', labelEn: 'Faster BoQ Processing', labelAr: 'أسرع في معالجة قوائم الكميات', icon: TrendingUp },
      { to: 60, suffix: '%', labelEn: 'Fewer Submittal Rejections', labelAr: 'انخفاض في رفض المستندات', icon: FileCheck },
      { to: 3,  suffix: 'x', labelEn: 'More Tenders Per Team', labelAr: 'أضعاف المناقصات لكل فريق', icon: Award },
    ],
    workflow: [
      { en: 'Upload BoQ', ar: 'رفع قائمة الكميات' },
      { en: 'Extract Items', ar: 'استخراج البنود' },
      { en: 'Normalize Units', ar: 'توحيد الوحدات' },
      { en: 'AI Catalog Match', ar: 'مطابقة الكتالوج' },
      { en: 'Review & Flag', ar: 'مراجعة وتمييز' },
      { en: 'Export to ERP', ar: 'تصدير للـ ERP' },
    ],
    image: '/industrial/diffusers.webp',
    href: '/industries/hvac',
  },
  mep: {
    icon: Wrench,
    color: 'emerald',
    titleEn: 'MEP & Engineering Teams',
    titleAr: 'فرق الأنظمة الميكانيكية والكهربائية والصرف',
    taglineEn: 'One Platform Across All Three Engineering Disciplines',
    taglineAr: 'منصة واحدة عبر التخصصات الهندسية الثلاثة',
    descEn: "MEP projects combine thousands of line items across Mechanical, Electrical, and Plumbing disciplines — each with distinct product families, sizing rules, and specification standards. Tuly brings automated classification, spec compliance analysis, and submittal generation into one governed team workspace.",
    descAr: 'مشاريع MEP تجمع آلاف البنود عبر تخصصات ميكانيكية وكهربائية وصرف، لكل منها مجموعات منتجات ومعايير مواصفات مختلفة. تولي يجمع التصنيف الآلي وتحليل الامتثال وإنشاء المستندات في مساحة عمل فريق واحدة محكومة.',
    pains: [
      { en: 'Thousands of multi-discipline line items managed manually in spreadsheets', ar: 'آلاف البنود متعددة التخصصات تُدار يدوياً في جداول بيانات' },
      { en: 'Spec vs. datasheet compliance is entirely manual — clause by clause', ar: 'مراجعة الامتثال بين المواصفات وبيانات المنتج يدوية بالكامل — بند بند' },
      { en: 'Different engineers produce inconsistent classifications across project branches', ar: 'مهندسون مختلفون ينتجون تصنيفات غير متسقة عبر فروع المشروع' },
      { en: 'Submittal rejections delay project timelines by weeks, not days', ar: 'رفض المستندات يؤخر جداول المشاريع بأسابيع وليس أياماً' },
    ],
    features: [
      { en: 'Multi-discipline classification: Mechanical, Electrical & Plumbing in one run', ar: 'تصنيف متعدد التخصصات: ميكانيكي وكهربائي وصرف في تشغيل واحد' },
      { en: 'Automated clause-by-clause spec-to-datasheet compliance analysis', ar: 'تحليل امتثال آلي بند بند بين المواصفات وبيانات المنتج' },
      { en: 'Role-based workflows: Engineer → Reviewer → Admin approval chain', ar: 'سير عمل قائمة على الأدوار: مهندس ← مراجع ← مشرف' },
      { en: 'Centralized project workspace with full versioned audit trail', ar: 'مساحة عمل مشاريع مركزية مع سجل مراجعة موثق ومُصدَّر' },
    ],
    stats: [
      { to: 70, suffix: '%', labelEn: 'Compliance Review Time Saved', labelAr: 'توفير في وقت مراجعة الامتثال', icon: Timer },
      { to: 4,  suffix: 'x', labelEn: 'More Projects Per Team', labelAr: 'أضعاف المشاريع لكل فريق', icon: TrendingUp },
      { to: 100,suffix: '%', labelEn: 'Decision Traceability', labelAr: 'قابلية تتبع القرارات', icon: Shield },
    ],
    workflow: [
      { en: 'Upload Specs', ar: 'رفع المواصفات' },
      { en: 'Upload Datasheets', ar: 'رفع بيانات المنتج' },
      { en: 'Structure Documents', ar: 'هيكلة الوثائق' },
      { en: 'Run Compliance', ar: 'تشغيل الامتثال' },
      { en: 'Review & Approve', ar: 'مراجعة واعتماد' },
      { en: 'Generate Submittal', ar: 'إنشاء المستند' },
    ],
    image: '/industrial/engineering drawings 1.avif',
    href: '/industries/mep',
  },
  manufacturing: {
    icon: Factory,
    color: 'amber',
    titleEn: 'Industrial Product Suppliers',
    titleAr: 'الموردون الصناعيون',
    taglineEn: 'Scale Across All Your Contractor Clients Without Scaling Headcount',
    taglineAr: 'توسّع عبر جميع عملاء المقاولين بدون تكبير الفريق',
    descEn: "Industrial product suppliers serve dozens of contractor clients simultaneously — each bringing a different BoQ format, naming convention, and submittal standard. Tuly's catalog-aware engine processes every client's requirements against your product range in parallel, turning one engineer's capacity into an entire department's output.",
    descAr: 'الموردون الصناعيون يخدمون عشرات من عملاء المقاولين في آنٍ واحد — بصيغ قوائم كميات مختلفة ومتطلبات مستندات مختلفة. محرك تولي يعالج متطلبات كل عميل مقابل نطاق منتجاتك بالتوازي.',
    pains: [
      { en: 'Serving 10+ contractor clients simultaneously — each with a different BoQ format', ar: 'خدمة أكثر من 10 مقاولين في آنٍ واحد — بصيغ قوائم كميات مختلفة' },
      { en: 'Every new project requires a fresh manual submittal package from scratch', ar: 'كل مشروع جديد يستلزم حزمة مستندات يدوية جديدة من الصفر' },
      { en: 'Engineers bottlenecked on repetitive documentation — no time for actual engineering', ar: 'المهندسون محتجزون في وثائق متكررة — لا وقت للهندسة الفعلية' },
      { en: 'No centralized visibility across all client approval pipelines', ar: 'لا رؤية مركزية عبر مسارات اعتماد المستندات لجميع العملاء' },
    ],
    features: [
      { en: 'Multi-client parallel project management from one unified dashboard', ar: 'إدارة مشاريع متعددة العملاء بالتوازي من لوحة تحكم موحدة' },
      { en: 'Catalog-aware AI that scales across complex multi-tier product hierarchies', ar: 'ذكاء اصطناعي يفهم الكتالوج ويتسع عبر تسلسلات المنتجات متعددة المستويات' },
      { en: 'Automated submittal generation for every contractor client project', ar: 'إنشاء مستندات آلي لكل مشروع مع عملاء المقاولين' },
      { en: 'ERP-ready outputs: Odoo, SAP, Oracle, Microsoft Dynamics', ar: 'مخرجات جاهزة للـ ERP: Odoo وSAP وOracle وDynamics' },
    ],
    stats: [
      { to: 5,  suffix: 'x', labelEn: 'More Bids Handled Per Team', labelAr: 'أضعاف العروض التي يديرها الفريق', icon: TrendingUp },
      { to: 80, suffix: '%', labelEn: 'Submittal Prep Time Saved', labelAr: 'توفير في وقت تحضير المستندات', icon: Timer },
      { to: 100,suffix: '%', labelEn: 'Catalog Consistency Across Clients', labelAr: 'اتساق الكتالوج عبر جميع العملاء', icon: Award },
    ],
    workflow: [
      { en: 'Client BoQ Upload', ar: 'رفع قائمة كميات العميل' },
      { en: 'Auto-Classify', ar: 'تصنيف تلقائي' },
      { en: 'Catalog Match', ar: 'مطابقة الكتالوج' },
      { en: 'Generate Submittal', ar: 'إنشاء المستند' },
      { en: 'ERP Export', ar: 'تصدير ERP' },
      { en: 'Track Status', ar: 'متابعة الحالة' },
    ],
    image: '/industrial/industrial ducting system.jpg',
    href: '/industries/manufacturing',
  },
  tender: {
    icon: ClipboardList,
    color: 'purple',
    titleEn: 'Tender & Estimation Teams',
    titleAr: 'فرق المناقصات والتقدير',
    taglineEn: 'Evaluate 10× More Bids In the Same Time — With Full Audit Trail',
    taglineAr: 'قيّم 10 أضعاف العروض في نفس الوقت — مع سجل مراجعة كامل',
    descEn: "Tender and estimation teams receive BoQs from 5–15 bidders under tight deadlines, each in a different format with different naming conventions. Manual normalization creates compounding errors that affect procurement decisions. Tuly normalizes all bids simultaneously and produces a standardized, evidence-backed comparison output.",
    descAr: 'فرق المناقصات والتقدير تستلم قوائم كميات من 5-15 مناقصاً تحت ضغط مواعيد، كل منها بصيغة ومصطلحات مختلفة. تولي ينسّق جميع العروض في وقت واحد وينتج مخرجات مقارنة موحدة مدعومة بالأدلة.',
    pains: [
      { en: 'Receiving BoQs from 5–15 bidders in different formats under tight deadlines', ar: 'استلام قوائم كميات من 5-15 مناقصاً بصيغ مختلفة تحت ضغط المواعيد' },
      { en: 'Manual normalization introduces errors that distort bid comparison', ar: 'التوحيد اليدوي يُدخل أخطاء تشوّه مقارنة العروض' },
      { en: 'No standardized evaluation format — each engineer does it differently', ar: 'لا صيغة تقييم موحدة — كل مهندس يتبع أسلوبه الخاص' },
      { en: 'No audit trail linking evaluation decisions back to source BoQ data', ar: 'لا سجل مراجعة يربط قرارات التقييم ببيانات قائمة الكميات المصدر' },
    ],
    features: [
      { en: 'Parallel normalization of all bidder BoQs simultaneously — not one by one', ar: 'توحيد متوازٍ لجميع قوائم كميات المناقصين في وقت واحد' },
      { en: 'Standardized unit conversion and product classification across all bids', ar: 'تحويل وحدات موحد وتصنيف منتجات عبر جميع العروض' },
      { en: 'Side-by-side bid comparison with AI-generated variance summary', ar: 'مقارنة جنباً إلى جنب مع ملخص فروقات مُنشأ بالذكاء الاصطناعي' },
      { en: 'Full audit trail connecting every evaluation decision to source BoQ data', ar: 'سجل مراجعة كامل يربط كل قرار تقييم بقائمة الكميات المصدر' },
    ],
    stats: [
      { to: 90, suffix: '%', labelEn: 'Normalization Time Saved', labelAr: 'توفير في وقت التوحيد', icon: Timer },
      { to: 10, suffix: 'x', labelEn: 'More Bids Evaluated In Same Time', labelAr: 'أضعاف العروض المقيّمة في نفس الوقت', icon: TrendingUp },
      { to: 100,suffix: '%', labelEn: 'Standardized Evaluation Format', labelAr: 'صيغة تقييم موحدة بالكامل', icon: GitCompare },
    ],
    workflow: [
      { en: 'Upload All Bids', ar: 'رفع جميع العروض' },
      { en: 'Normalize Formats', ar: 'توحيد الصيغ' },
      { en: 'Classify Products', ar: 'تصنيف المنتجات' },
      { en: 'Compare Side-by-Side', ar: 'مقارنة جنباً لجنب' },
      { en: 'Flag Variances', ar: 'تمييز الفروقات' },
      { en: 'Procurement Report', ar: 'تقرير الشراء' },
    ],
    image: '/industrial/engineering drawings 2.jpg',
    href: '/industries/tender',
  },
};

const INDUSTRY_KEYS = ['hvac', 'mep', 'manufacturing', 'tender'];

const TAB_LABELS = {
  hvac:          { en: 'HVAC',               ar: 'التكييف' },
  mep:           { en: 'MEP Engineering',    ar: 'MEP' },
  manufacturing: { en: 'Industrial Suppliers', ar: 'الموردون' },
  tender:        { en: 'Tender Teams',       ar: 'المناقصات' },
};

/* ─── main component ─── */

export default function Industries() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const [activeIndustry, setActiveIndustry] = useState('hvac');

  /* Sector gallery auto-slider */
  const sliderRef = useRef(null);
  const sliderPosRef = useRef(0);
  const sliderRafRef = useRef(null);
  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const tick = () => {
      sliderPosRef.current += 0.6;
      if (sliderPosRef.current >= el.scrollWidth / 2) sliderPosRef.current = 0;
      el.style.transform = `translateX(-${sliderPosRef.current}px)`;
      sliderRafRef.current = requestAnimationFrame(tick);
    };
    sliderRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(sliderRafRef.current);
  }, []);

  const ind = INDUSTRIES[activeIndustry];
  const colors = IND_COLORS[ind.color];

  /* KPI data */
  const kpis = [
    { to: 85, suffix: '%', labelEn: 'Average BoQ processing time saved', labelAr: 'متوسط الوقت الموفر في معالجة قوائم الكميات', icon: BarChart2, color: 'text-primary', bg: 'bg-primary/8' },
    { to: 3,  suffix: 'x', labelEn: 'Faster submittal approvals across sectors', labelAr: 'أسرع في اعتماد المستندات عبر القطاعات', icon: Zap, color: 'text-accent', bg: 'bg-accent/8' },
    { to: 100,suffix: '%', labelEn: 'Decision traceability — every match logged', labelAr: 'قابلية تتبع القرارات — كل مطابقة موثقة', icon: Shield, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { to: 4,  suffix: '+', labelEn: 'Industrial sectors served and growing', labelAr: 'قطاعات صناعية تخدمها المنصة وتتوسع', icon: Layers, color: 'text-blue-500', bg: 'bg-sky-50' },
  ];

  /* Why Tuly */
  const sharedValues = [
    {
      icon: Cpu,
      titleEn: 'Domain-Trained AI',
      titleAr: 'ذكاء اصطناعي مدرب على القطاع',
      descEn: "Classification models trained on real industrial product data — HVAC catalogs, MEP specs, industrial nomenclature. Not generic AI.",
      descAr: 'نماذج تصنيف مدربة على بيانات منتجات صناعية حقيقية — كتالوجات HVAC، مواصفات MEP، تسميات صناعية. ليس ذكاءً اصطناعياً عاماً.',
    },
    {
      icon: Shield,
      titleEn: 'Governed by Design',
      titleAr: 'حوكمة بالتصميم',
      descEn: 'Every AI decision carries a confidence score and a full audit trail. Compliance is baked into the architecture — not added later.',
      descAr: 'كل قرار للذكاء الاصطناعي يحمل تقييم ثقة وسجل مراجعة كامل. الامتثال مدمج في البنية الأساسية، ليس إضافة لاحقة.',
    },
    {
      icon: Users,
      titleEn: 'Built for Teams',
      titleAr: 'مبني للفرق',
      descEn: 'Role-based workflows for Engineers, Reviewers, and Admins — structured collaboration with no email chains or version confusion.',
      descAr: 'سير عمل قائمة على الأدوار للمهندسين والمراجعين والمشرفين — تعاون منظم بدون سلاسل بريد أو ارتباك في الإصدارات.',
    },
    {
      icon: CheckCircle,
      titleEn: 'ERP-Ready Outputs',
      titleAr: 'مخرجات جاهزة للـ ERP',
      descEn: 'Outputs formatted for direct import into Odoo, SAP, Oracle, and Dynamics — zero manual re-entry, zero field-mapping work.',
      descAr: 'مخرجات مُنسَّقة للاستيراد المباشر في Odoo وSAP وOracle وDynamics — صفر إدخال يدوي مجدد، صفر جهد تعيين حقول.',
    },
  ];

  /* coverage matrix */
  const coverageRows = [
    { featureEn: 'BoQ Conversion',         featureAr: 'تحويل قوائم الكميات',        hvac: true,  mep: true,  manufacturing: true,  tender: true  },
    { featureEn: 'Technical Submittals',   featureAr: 'المستندات التقنية',           hvac: true,  mep: true,  manufacturing: false,  tender: true },
    { featureEn: 'Spec Compliance Analysis',featureAr: 'تحليل امتثال المواصفات',    hvac: true, mep: true,  manufacturing: true,  tender: true },
    { featureEn: 'ERP Export',             featureAr: 'تصدير ERP',                  hvac: true,  mep: true,  manufacturing: true,  tender: false },
    { featureEn: 'Catalog AI Mapping',     featureAr: 'ربط الكتالوج بالذكاء الاصطناعي', hvac: true, mep: true, manufacturing: true, tender: true },
    { featureEn: 'Full Audit Trail',       featureAr: 'سجل مراجعة كامل',           hvac: true,  mep: true,  manufacturing: true,  tender: true  },
    { featureEn: 'Role-Based Workflows',   featureAr: 'سير عمل قائمة على الأدوار', hvac: true,  mep: true,  manufacturing: true,  tender: true  },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="industries" />

      {/* ── HERO ── */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden bg-midnight -mt-16 pt-16">
        <div className="absolute inset-0 blueprint-grid-dark opacity-40" />
        <div className="absolute top-1/3 left-1/3 w-[700px] h-[400px] rounded-full bg-primary/10 blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[300px] rounded-full bg-accent/6 blur-[100px] pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge variant="dark"><Building2 size={12} />{lang === 'ar' ? 'القطاعات الصناعية' : 'Industrial Sectors'}</Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-[3.2rem] font-black text-white leading-[1.06] tracking-tight"
            >
              {lang === 'ar' ? (
                <>مبني لـ<span className="gradient-text"> قطاعات صناعية</span> حقيقية</>
              ) : (
                <>Built for <span className="gradient-text">Real Industrial</span> Sectors</>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-5 text-base sm:text-lg text-white/60 leading-relaxed max-w-xl"
            >
              {lang === 'ar'
                ? 'تولي مصمم للقطاعات الأكثر تعقيداً من حيث الوثائق والمنتجات — مع ذكاء اصطناعي مدرب على مصطلحات كل قطاع.'
                : 'Tuly is purpose-built for the most document and product-intensive industrial sectors — with AI trained on the specific terminology of each.'}
            </motion.p>

            {/* Industry pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-7 flex flex-wrap gap-2"
            >
              {INDUSTRY_KEYS.map((key, i) => {
                const d = INDUSTRIES[key];
                return (
                  <motion.button
                    key={key}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + i * 0.07 }}
                    onClick={() => { setActiveIndustry(key); document.getElementById('industry-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-white/80 border border-white/15 hover:bg-white/18 hover:text-white transition-all"
                  >
                    <d.icon size={11} />
                    {lang === 'ar' ? TAB_LABELS[key].ar : TAB_LABELS[key].en}
                  </motion.button>
                );
              })}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
              >
                {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
              </button>
              <button
                onClick={() => navigate('/solutions')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
              >
                {lang === 'ar' ? 'استكشف الحلول' : 'Explore Solutions'}
              </button>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
        >
          <span className="text-white/25 text-[10px] tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* ── INDUSTRY SELECTOR ── */}
      <Section id="industry-selector" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="primary"><Target size={12} />{lang === 'ar' ? 'اختر قطاعك' : 'Your Industry'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'تولي مدرّب على قطاعك تحديداً' : 'Tuly Is Trained for Your Specific Sector'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-lg mx-auto leading-relaxed">
              {lang === 'ar'
                ? 'كل قطاع صناعي له منتجاته وتحدياته وأعرافه الوثائقية الخاصة — تولي مدرّب عليها جميعاً.'
                : 'Every industrial sector has its own products, challenges, and document conventions — Tuly is trained for all of them.'}
            </p>
          </div>

          {/* Tab buttons */}
          {(() => {
            const TAB_META = {
              hvac:          { shortEn: 'Equipment matching',    shortAr: 'مطابقة المعدات'    },
              mep:           { shortEn: 'Submittal automation',  shortAr: 'أتمتة التقديمات'  },
              manufacturing: { shortEn: 'Catalog sync',          shortAr: 'مزامنة الكتالوج'  },
              tender:        { shortEn: 'BoQ estimation',        shortAr: 'تسعير الكميات'    },
            };
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
                {INDUSTRY_KEYS.map((key) => {
                  const d = INDUSTRIES[key];
                  const c = IND_COLORS[d.color];
                  const isActive = activeIndustry === key;
                  const meta = TAB_META[key];
                  return (
                    <motion.button
                      key={key}
                      onClick={() => setActiveIndustry(key)}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      className={`relative flex flex-col items-start gap-3 p-5 rounded-2xl border text-left transition-all duration-300 overflow-hidden ${
                        isActive
                          ? `bg-white ${c.border} shadow-lg ${c.glow}`
                          : 'bg-white border-border-light hover:border-slate-200 hover:shadow-sm'
                      }`}
                    >
                      {/* Active top accent bar (shared layout animation) */}
                      {isActive && (
                        <motion.div
                          layoutId="tab-accent"
                          className={`absolute top-0 left-0 right-0 h-[3px] ${c.tab.split(' ')[0]}`}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                      )}
                      {/* Icon box */}
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        isActive ? c.tab : `${c.bg} ${c.text}`
                      }`}>
                        <d.icon size={20} />
                      </div>
                      {/* Label + descriptor */}
                      <div>
                        <p className={`font-bold text-sm leading-tight transition-colors ${isActive ? 'text-heading' : 'text-body'}`}>
                          {lang === 'ar' ? TAB_LABELS[key].ar : TAB_LABELS[key].en}
                        </p>
                        <p className={`text-[11px] mt-1 transition-colors ${isActive ? c.text : 'text-slate-400'}`}>
                          {lang === 'ar' ? meta.shortAr : meta.shortEn}
                        </p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            );
          })()}

          {/* Animated content panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndustry}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.38, ease: 'easeOut' }}
              className="bg-white rounded-3xl border border-border-light shadow-card overflow-hidden"
            >
              <div className="grid lg:grid-cols-[1fr_360px]">

                {/* Left: content */}
                <div className="p-8 lg:p-12">

                  {/* Badge + tagline */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border mb-5 ${colors.pill}`}>
                    <ind.icon size={12} />
                    {lang === 'ar' ? ind.titleAr : ind.titleEn}
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-heading leading-tight mb-3">
                    {lang === 'ar' ? ind.taglineAr : ind.taglineEn}
                  </h3>
                  <p className="text-body text-sm leading-relaxed mb-8 max-w-lg">
                    {lang === 'ar' ? ind.descAr : ind.descEn}
                  </p>

                  {/* Before / After columns */}
                  <div className="grid sm:grid-cols-2 gap-6 mb-8 p-6 rounded-2xl bg-slate-50 border border-border-light">
                    {/* Pain points */}
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-red-400 mb-4">
                        {lang === 'ar' ? '— بدون تولي' : '— Without Tuly'}
                      </p>
                      <ul className="space-y-3.5">
                        {ind.pains.map((p, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: isRTL ? 10 : -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.08 + i * 0.06 }}
                            className="flex items-start gap-2.5"
                          >
                            <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-body leading-snug">{lang === 'ar' ? p.ar : p.en}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                    {/* Features */}
                    <div>
                      <p className={`text-[10px] font-black uppercase tracking-[0.18em] mb-4 ${colors.text}`}>
                        {lang === 'ar' ? '+ مع تولي' : '+ With Tuly'}
                      </p>
                      <ul className="space-y-3.5">
                        {ind.features.map((f, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: isRTL ? -10 : 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.12 + i * 0.06 }}
                            className="flex items-start gap-2.5"
                          >
                            <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-body leading-snug">{lang === 'ar' ? f.ar : f.en}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Workflow steps */}
                  <div className="mb-8">
                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted mb-3">
                      {lang === 'ar' ? 'كيف يعمل تولي' : 'How It Works'}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {ind.workflow.map((step, i) => (
                        <React.Fragment key={i}>
                          <motion.span
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 + i * 0.055 }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}
                          >
                            {i + 1}. {lang === 'ar' ? step.ar : step.en}
                          </motion.span>
                          {i < ind.workflow.length - 1 && (
                            <ChevronRight size={13} className={`text-muted flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => navigate('/contact')}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-hover transition-all shadow-primary-glow"
                    >
                      {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                      <ArrowRight size={14} className={isRTL ? 'rotate-180' : ''} />
                    </button>
                    <button
                      onClick={() => navigate(ind.href)}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all border ${colors.bg} ${colors.text} ${colors.border}`}
                    >
                      {lang === 'ar' ? 'اكتشف المزيد' : 'Deep Dive'}
                      <ArrowRight size={14} className={isRTL ? 'rotate-180' : ''} />
                    </button>
                  </div>
                </div>

                {/* Right: image + stats sidebar */}
                <div className="hidden lg:flex flex-col">
                  <div className="relative flex-1 overflow-hidden min-h-[280px]">
                    <img
                      src={ind.image}
                      alt={lang === 'ar' ? ind.titleAr : ind.titleEn}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60" />
                    <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${colors.tab} shadow-lg`}>
                        <ind.icon size={11} />
                        {lang === 'ar' ? TAB_LABELS[activeIndustry].ar : TAB_LABELS[activeIndustry].en}
                      </span>
                    </div>
                  </div>

                  {/* Stats panel */}
                  <div className={`${colors.bg} border-t ${colors.border} p-6 space-y-4`}>
                    {ind.stats.map((s, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + i * 0.08 }}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <s.icon size={13} className={`${colors.text} flex-shrink-0`} />
                          <span className="text-xs text-body leading-tight">{lang === 'ar' ? s.labelAr : s.labelEn}</span>
                        </div>
                        <span className={`text-2xl font-black tabular-nums ${colors.text}`}>
                          <Counter to={s.to} suffix={s.suffix} />
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile stats row */}
              <div className={`lg:hidden grid grid-cols-3 border-t ${colors.border}`}>
                {ind.stats.map((s, i) => (
                  <div key={i} className={`p-4 text-center ${i > 0 ? `border-l ${colors.border}` : ''} ${colors.bg}`}>
                    <p className={`text-2xl font-black tabular-nums ${colors.text}`}>
                      <Counter to={s.to} suffix={s.suffix} />
                    </p>
                    <p className="text-[10px] text-muted mt-1 leading-tight px-1">{lang === 'ar' ? s.labelAr : s.labelEn}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Section>

      {/* ── FEATURE COVERAGE MATRIX ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="primary"><LayoutDashboard size={12} />{lang === 'ar' ? 'تغطية الميزات' : 'Feature Coverage'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'ما تحصل عليه في كل قطاع' : 'What You Get in Each Sector'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-lg mx-auto">
              {lang === 'ar'
                ? 'قدرات تولي مصممة لتلبية الاحتياجات الفريدة لكل قطاع صناعي.'
                : "Tuly's capabilities are matched to each sector's unique operational needs."}
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border-light shadow-card">
            <table className="w-full min-w-[620px]">
              <thead>
                <tr className="bg-slate-50 border-b border-border-light">
                  <th className={`${isRTL ? 'text-right pr-6' : 'text-left pl-6'} py-4 text-xs font-bold uppercase tracking-widest text-muted w-48`}>
                    {lang === 'ar' ? 'الميزة' : 'Feature'}
                  </th>
                  {[
                    { key: 'hvac',          icon: Building2,    label: { en: 'HVAC', ar: 'التكييف' },         color: 'text-blue-600' },
                    { key: 'mep',           icon: Wrench,       label: { en: 'MEP', ar: 'MEP' },             color: 'text-emerald-600' },
                    { key: 'manufacturing', icon: Factory,      label: { en: 'Suppliers', ar: 'الموردون' },  color: 'text-amber-600' },
                    { key: 'tender',        icon: ClipboardList,label: { en: 'Tender', ar: 'المناقصات' },   color: 'text-purple-600' },
                  ].map(col => (
                    <th key={col.key} className="py-4 px-4 text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        <col.icon size={16} className={col.color} />
                        <span className="text-xs font-bold text-heading">
                          {lang === 'ar' ? col.label.ar : col.label.en}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coverageRows.map((row, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className={`border-b border-border-light last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                  >
                    <td className={`${isRTL ? 'pr-6 text-right' : 'pl-6 text-left'} py-3.5 text-sm font-medium text-body`}>
                      {lang === 'ar' ? row.featureAr : row.featureEn}
                    </td>
                    {[row.hvac, row.mep, row.manufacturing, row.tender].map((has, j) => (
                      <td key={j} className="py-3.5 px-4 text-center">
                        {has ? (
                          <CheckCircle size={16} className="text-emerald-500 mx-auto" />
                        ) : (
                          <span className="block w-4 h-px bg-border-light mx-auto" />
                        )}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* ── WHY TULY ── */}
      <Section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Zap size={12} />{lang === 'ar' ? 'لماذا تولي' : 'Why Tuly'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'لماذا الفرق الصناعية تختار تولي' : 'Why Industrial Teams Choose Tuly'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-lg mx-auto leading-relaxed">
              {lang === 'ar'
                ? 'تولي ليست أداة ذكاء اصطناعي عامة — بل منصة متخصصة مبنية للتحديات الدقيقة لقطاع البناء والصناعة الثقيلة.'
                : "Tuly is not a generic AI tool — it's a specialized platform built for the precise challenges of construction and heavy industry."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sharedValues.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="bg-white rounded-2xl p-6 border border-border-light shadow-card hover:shadow-card-hover transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/8 flex items-center justify-center mb-5">
                  <v.icon size={22} className="text-primary" />
                </div>
                <h3 className="text-heading font-bold text-base mb-3">{lang === 'ar' ? v.titleAr : v.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? v.descAr : v.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── INDUSTRY QUICK CARDS ── */}
      <Section className="py-20 bg-white overflow-hidden">
        <div className="text-center mb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge variant="primary"><PackageSearch size={12} />{lang === 'ar' ? 'استكشاف القطاعات' : 'Explore Sectors'}</Badge>
          <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
            {lang === 'ar' ? 'ادخل إلى قطاعك' : 'Go Deeper Into Your Sector'}
          </h2>
        </div>
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          <div ref={sliderRef} className="flex gap-5 px-6" style={{ width: 'max-content' }}>
            {[...INDUSTRY_KEYS, ...INDUSTRY_KEYS].map((key, i) => {
              const d = INDUSTRIES[key];
              const c = IND_COLORS[d.color];
              return (
                <div
                  key={i}
                  className="relative rounded-2xl overflow-hidden flex-shrink-0 w-80 h-80 shadow-card"
                >
                  <img
                    src={d.image}
                    alt={lang === 'ar' ? d.titleAr : d.titleEn}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-between">
                    <span className={`self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${c.tab} opacity-90`}>
                      <d.icon size={10} />
                      {lang === 'ar' ? TAB_LABELS[key].ar : TAB_LABELS[key].en}
                    </span>
                    <h3 className="text-white font-bold text-base leading-snug">
                      {lang === 'ar' ? d.taglineAr : d.taglineEn}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ── CTA ── */}
      <Section className="py-24 bg-midnight relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-35" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/10 blur-[100px] rounded-full" />

        <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6 border border-white/15"
          >
            <img src="/tuly.png" alt="Tuly Technology" className="w-10 h-10 object-contain" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-black text-white"
          >
            {lang === 'ar' ? 'قطاعك الصناعي. كفاءة أعلى.' : 'Your Industry. A Higher Level of Efficiency.'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-white/55 leading-relaxed text-sm max-w-xl mx-auto"
          >
            {lang === 'ar'
              ? 'احجز عرضاً مباشراً مصمماً لقطاعك — ونريك كيف تعمل تولي مع منتجاتك وعملياتك المحددة.'
              : "Book a live demo tailored to your sector — we'll show you exactly how Tuly works with your specific products, catalog, and document workflows."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-8 flex flex-wrap gap-3 justify-center"
          >
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
            >
              {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              onClick={() => navigate('/solutions')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/8 transition-all"
            >
              {lang === 'ar' ? 'استكشف الحلول' : 'Explore Solutions'}
            </button>
          </motion.div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
                <span className="text-white font-bold text-sm">Tuly Technology</span>
              </div>
              <p className="text-muted text-xs leading-relaxed">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                {[['boq', '/solutions/boq'], ['submittals', '/solutions/submittals'], ['platform', '/platform']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                {[['about', '/about'], ['contact', '/contact']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.legal')}</h4>
              <ul className="space-y-2.5">
                {[['privacy', '/privacy'], ['terms', '/terms']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-dark-border flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
            <p className="text-muted/50 text-xs">{t('footer.madeWith')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
